<template>
  <div id="batch-history">
    <div id="batch-title">
      <h1>Batch History</h1>
      <a-button @click="toggleModal">Run Batch</a-button>
    </div>
    <a-table
      :columns="columns"
      :data-source="data"
      class="components-table-demo-nested"
      :pagination="false"
      @expand="fetchLogsPerBatch"
      :rowKey="record => record.LeadGenId"
      :loading="isLoading"
    >
      <div class="center-col" slot="startedAt" slot-scope="record">{{formatDate(record.CreatedAt)}}</div>
      <div class="center-col" slot="type" slot-scope="record">{{(record.BatchType || 'Automatic')}}</div>
      <div class="center-col" slot="numDays" slot-scope="record">{{(record.NumDays || 3)}}</div>
      <div class="center-col" slot="status" slot-scope="record">{{formatStatus(record.StatusDate)}}</div>
      <div class="center-col" slot="expandedRowRender" slot-scope="record">
        <batch-forms :record="record" />
      </div>
    </a-table>
    <a-modal
      title="Run Batch"
      :visible="modalVisible"
      :confirm-loading="modalLoading"
      @cancel="toggleModal"
      width="50vw"
    >
      <template slot="footer">
        <a-button key="back" @click="toggleModal">Cancel</a-button>
      </template>
      <div class="modal-body">
        This allows you to manually kick off a batch. You may select a specific date in the last 30-days and how many days previous
        to that date you'd like it to go. A batch will start and end at 08:00 UTC. If a lead has already been processed it will not be
        reprocessed.
        <p class="modal-content">
          End Date:
          <a-date-picker
            class="selection"
            :format="dateFormat"
            :disabledDate="disabledDate"
            @change="setEndDate"
          />Go Back:
          <a-select class="selection" @change="changeGoBackDays">
            <a-select-option key="3">3 days</a-select-option>
            <a-select-option key="2">2 days</a-select-option>
            <a-select-option key="1">1 day</a-select-option>
          </a-select>

          <a-button @click="runBatch()" type="primary" :disabled="startDate == null">Run Batch</a-button>
        </p>
        <p
          v-if="!!startDate"
        >Leads will be processed from {{formatMMMMDDYYYY(startDate)}} 08:00 UTC to {{formatMMMMDDYYYY(endDate)}} 08:00 UTC</p>
      </div>
    </a-modal>
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import { mapState } from "vuex";
import axios from "axios";
import BatchForms from "../components/BatchForms";

const columns = [
  { title: "Batch Id", dataIndex: "LeadGenId", key: "LeadGenId" },
  {
    title: "Started at",
    key: "StartedAt",
    scopedSlots: { customRender: "startedAt" },
  },
  {
    title: "Type",
    key: "Type",
    scopedSlots: { customRender: "type" },
  },
  {
    title: "Num. Days",
    key: "NumDays",
    scopedSlots: { customRender: "numDays" },
  },
  {
    title: "Leads Found",
    dataIndex: "LeadsFound",
    key: "LeadsFound",
    className: "center-col",
  },
  {
    title: "Leads Processed",
    dataIndex: "LeadsProcessed",
    key: "LeadsProcessed",
    className: "center-col",
  },
  {
    title: "Leads Failed",
    dataIndex: "LeadsErrored",
    key: "LeadsErrored",
    className: "center-col",
  },
  { title: "Status", key: "Status", scopedSlots: { customRender: "status" } },
  {
    title: "Forms Processed",
    dataIndex: "FormsProcessed",
    key: "FormsProcessed",
    className: "center-col",
  },
];

export default {
  name: "BatchHistory",
  components: { BatchForms },
  computed: {
    ...mapState(["user", "apiUrl"]),
  },
  data() {
    return {
      columns,
      data: [],
      expandedBatch: [],
      isLoading: false,
      expandedLoading: false,
      expandedRows: [],
      modalVisible: false,
      modalLoading: false,
      dateFormat: "YYYY-MM-DD",
      startDate: null,
      endDate: null,
      goBackDay: null,
    };
  },
  mounted() {
    this.isLoading = true;
    this.fetchLogs();
  },
  methods: {
    fetchLogs() {
      this.isLoading = true;
      axios({
        url: `${this.apiUrl}/logs`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          formId: "BATCH",
          action: "BATCH_LOGS",
          processor: "facebook",
        },
      })
        .then((rslt) => {
          this.data = rslt.data;
          this.isLoading = false;
        })
        .catch((err) => {
          alert("Unable to load logs");
          this.isLoading = false;
        });
    },
    formatDate(d) {
      const date = new Date(d);
      return date.toLocaleString();
    },
    formatStatus(status) {
      if (status == "B-InProcess") {
        return "In process";
      }
      return "Completed";
    },
    formatMMMMDDYYYY(date) {
      return moment(date).format("MMMM DD, YYYY");
    },
    fetchLogsPerBatch(expanded, record) {
      if (expanded) {
        this.expandedLoading = true;
        axios({
          url: `${this.apiUrl}/logs`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            batchId: record.LeadGenId,
            action: "LOGS_PER_BATCH",
            processor: "facebook",
          },
        })
          .then((rslt) => {
            this.expandedBatch = rslt.data;
            this.expandedLoading = false;
            this.expandedRows = [record.LeadGenId];
          })
          .catch((err) => {
            alert("Unable to load logs");
            this.expandedLoading = false;
          });
      }
    },
    toggleModal() {
      this.modalVisible = !this.modalVisible;
    },
    setEndDate(date) {
      this.endDate = date;
      this.setStartDate();
    },
    setStartDate() {
      if (this.endDate != null && this.goBackDay != null) {
        this.startDate = moment(this.endDate).subtract(this.goBackDay, "day");
      }
    },
    changeGoBackDays(val) {
      this.goBackDay = val.split(" ")[0];

      this.setStartDate();
    },
    disabledDate(current) {
      return current && current > moment().endOf("day");
    },
    runBatch() {
      axios({
        method: "post",
        url: `${this.apiUrl}/runBatchManual`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          endDate: moment(this.endDate).format("YYYYMMDD"),
          numOfDays: this.goBackDay,
        },
      });

      this.toggleModal();
      this.fetchLogs();
    },
  },
};
</script>

<style lang="scss" scoped>
#batch-title {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  h1 {
    margin: 0 10px 0 0;
  }
}
.modal-content {
  display: flex;
  margin: 20px;
  justify-content: space-evenly;

  .selection {
    width: 130px;
  }
}

::v-deep {
  .ant-table-thead > tr > th,
  .center-col {
    text-align: center;
    &[key="PageName"] {
      text-align: left;
    }
  }
}
</style>